/**
 * Singleton WebSocket client. Один WS на всё приложение.
 * Покрывает: auth, friends, rooms, lock-step input, leaderboard.
 */

export type Role = "p1" | "p2";

export interface InputSnapshot {
  l: boolean; r: boolean; u: boolean; d: boolean;
  p: boolean; k: boolean; b: boolean;
}

export interface PlayerInfo {
  playerId: string;
  login: string;
  online: boolean;
  wins?: number;
  losses?: number;
  bandanaColor?: string;
}

export type MpEvent =
  | { type: "connected" }
  | { type: "registered"; playerId: string; login: string; bandanaColor?: string; friends: PlayerInfo[]; wins: number; losses: number; opponents: PlayerInfo[] }
  | { type: "logged_in";  playerId: string; login: string; bandanaColor?: string; friends: PlayerInfo[]; wins: number; losses: number; opponents: PlayerInfo[] }
  | { type: "login_updated"; login: string }
  | { type: "password_updated" }
  | { type: "bandana_updated"; color: string }
  | { type: "friends"; friends: PlayerInfo[] }
  | { type: "opponents"; opponents: PlayerInfo[] }
  | { type: "leaderboard"; entries: { login: string; wins: number; losses: number }[] }
  | { type: "stats"; wins: number; losses: number }
  | { type: "friend_added"; friend: PlayerInfo }
  | { type: "friend_renamed"; playerId: string; login: string }
  | { type: "friend_status"; playerId: string; online: boolean }
  | { type: "friend_invite"; from: PlayerInfo; roomCode: string }
  | { type: "invite_sent"; roomCode: string; friend: PlayerInfo }
  | { type: "created"; roomCode: string; role: Role }
  | { type: "joined"; role: Role; opponentPlayerId?: string; opponentLogin?: string }
  | { type: "opponent_ready"; opponentPlayerId?: string; opponentLogin?: string }
  | { type: "select"; slug: string; from: Role }
  | { type: "start"; from: Role }
  | { type: "input"; frame: number; input: InputSnapshot; from: Role }
  | { type: "opponent_left" }
  | { type: "end"; reason: string; from?: Role }
  | { type: "error"; msg: string }
  | { type: "pong"; t: number };

export type MpListener = (e: MpEvent) => void;

export class MultiplayerClient {
  private ws?: WebSocket;
  private listeners: Set<MpListener> = new Set();
  role?: Role;
  roomCode?: string;
  rttMs = 0;
  private pingTimer?: any;

  // identity
  playerId?: string;
  login?: string;
  wins = 0;
  losses = 0;
  bandanaColor = "#c41e3a";
  friends: PlayerInfo[] = [];
  opponents: PlayerInfo[] = [];

  // last opponent in current room (для match_result)
  lastOpponentPlayerId?: string;
  lastOpponentLogin?: string;

  private wsUrl(): string {
    const proto = location.protocol === "https:" ? "wss:" : "ws:";
    return `${proto}//${location.host}/ws`;
  }

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) { resolve(); return; }
      const url = this.wsUrl();
      const ws = new WebSocket(url);
      let settled = false;
      // Не ждём 30+ секунд браузерного fail — если за 8с нет open, считаем недоступным.
      // Сцены catch'ат reject и показывают «Сервер недоступен».
      const timeout = setTimeout(() => {
        if (settled) return;
        settled = true;
        try { ws.close(); } catch (_e) {}
        reject(new Error("ws timeout"));
      }, 8000);
      ws.onopen = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        this.emit({ type: "connected" });
        this.startPing();
        resolve();
      };
      ws.onerror = (e) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        reject(e);
      };
      ws.onmessage = (msg) => {
        try {
          const data = JSON.parse(msg.data);
          this.absorb(data);
          this.emit(data);
        } catch (e) { console.warn("bad mp msg", e); }
      };
      ws.onclose = () => {
        this.stopPing();
        this.emit({ type: "end", reason: "ws_closed" });
      };
      this.ws = ws;
    });
  }

  private absorb(data: any) {
    switch (data.type) {
      case "registered":
      case "logged_in":
        this.playerId = data.playerId;
        this.login = data.login;
        this.bandanaColor = data.bandanaColor || "#c41e3a";
        this.wins = data.wins ?? 0;
        this.losses = data.losses ?? 0;
        this.friends = data.friends || [];
        this.opponents = data.opponents || [];
        break;
      case "bandana_updated":
        this.bandanaColor = data.color || this.bandanaColor;
        break;
      case "login_updated":
        this.login = data.login;
        break;
      case "friends":
        this.friends = data.friends || [];
        break;
      case "opponents":
        this.opponents = data.opponents || [];
        break;
      case "stats":
        this.wins = data.wins ?? this.wins;
        this.losses = data.losses ?? this.losses;
        break;
      case "friend_added": {
        const f: PlayerInfo = data.friend;
        const idx = this.friends.findIndex(x => x.playerId === f.playerId);
        if (idx >= 0) this.friends[idx] = f; else this.friends.push(f);
        break;
      }
      case "friend_renamed": {
        const f = this.friends.find(x => x.playerId === data.playerId);
        if (f) f.login = data.login;
        const o = this.opponents.find(x => x.playerId === data.playerId);
        if (o) o.login = data.login;
        break;
      }
      case "friend_status": {
        const f = this.friends.find(x => x.playerId === data.playerId);
        if (f) f.online = data.online;
        break;
      }
      case "created":
        this.roomCode = data.roomCode;
        this.role = data.role;
        break;
      case "joined":
        this.role = data.role;
        if (data.opponentPlayerId) {
          this.lastOpponentPlayerId = data.opponentPlayerId;
          this.lastOpponentLogin = data.opponentLogin;
        }
        break;
      case "opponent_ready":
        if (data.opponentPlayerId) {
          this.lastOpponentPlayerId = data.opponentPlayerId;
          this.lastOpponentLogin = data.opponentLogin;
        }
        break;
      case "invite_sent":
        this.roomCode = data.roomCode;
        this.role = "p1";
        if (data.friend?.playerId) {
          this.lastOpponentPlayerId = data.friend.playerId;
          this.lastOpponentLogin = data.friend.login;
        }
        break;
      case "pong":
        this.rttMs = Date.now() - data.t;
        break;
    }
  }

  // ── account ──
  register(login: string, password: string) { this.send({ type: "register", login, password }); }
  loginUser(login: string, password: string) { this.send({ type: "login", login, password }); }
  updateLogin(newLogin: string, password: string) { this.send({ type: "update_login", newLogin, password }); }
  updatePassword(oldPassword: string, newPassword: string) { this.send({ type: "update_password", oldPassword, newPassword }); }
  updateBandana(color: string) { this.send({ type: "update_bandana", color }); }

  // ── social ──
  fetchFriends()    { this.send({ type: "friends_list" }); }
  fetchOpponents()  { this.send({ type: "opponents_list" }); }
  fetchLeaderboard(limit = 10) { this.send({ type: "leaderboard", limit }); }
  addFriend(login: string) { this.send({ type: "add_friend", login }); }
  inviteFriend(playerId: string) { this.send({ type: "invite_friend", playerId }); }

  // ── rooms ──
  createRoom() { this.send({ type: "create" }); }
  joinRoom(roomCode: string) { this.roomCode = roomCode; this.send({ type: "join", roomCode }); }
  leaveRoom() {
    if (!this.roomCode) return;
    this.send({ type: "leave_room" });
    this.roomCode = undefined; this.role = undefined;
    this.lastOpponentPlayerId = undefined; this.lastOpponentLogin = undefined;
  }
  sendSelect(slug: string) { this.send({ type: "select", slug }); }
  sendStart() { this.send({ type: "start" }); }
  sendInput(frame: number, input: InputSnapshot) { this.send({ type: "input", frame, input }); }
  sendEnd(reason: string) { this.send({ type: "end", reason }); }

  // ── stats ──
  reportMatchResult(opponentPlayerId: string | undefined, won: boolean) {
    this.send({ type: "match_result", opponentPlayerId: opponentPlayerId || "", won });
  }

  private send(obj: any) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify(obj));
  }

  on(listener: MpListener) { this.listeners.add(listener); return () => this.listeners.delete(listener); }
  off(listener: MpListener) { this.listeners.delete(listener); }

  private emit(e: MpEvent) {
    for (const l of this.listeners) {
      try { l(e); } catch (err) { console.error("mp listener error", err); }
    }
  }

  private startPing() {
    this.pingTimer = setInterval(() => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
      this.send({ type: "ping", t: Date.now() });
    }, 2000);
  }
  private stopPing() {
    if (this.pingTimer) { clearInterval(this.pingTimer); this.pingTimer = undefined; }
  }

  close() {
    this.stopPing();
    try { this.ws?.close(); } catch (_e) {}
  }

  isOpen() { return this.ws?.readyState === WebSocket.OPEN; }
  isAuthed() { return !!this.playerId; }
}

let _shared: MultiplayerClient | null = null;
export function getMpClient(): MultiplayerClient {
  if (!_shared) _shared = new MultiplayerClient();
  return _shared;
}

import type { Controls } from "../input/Controls";
export function snapshotInput(c: Controls): InputSnapshot {
  return {
    l: c.isLeft(), r: c.isRight(), u: c.isUp(), d: c.isDown(),
    p: c.isPunch(), k: c.isKick(), b: c.isBlock(),
  };
}

export class RemoteControls implements Controls {
  readonly source: string = "REMOTE";
  private cur: InputSnapshot = { l:false, r:false, u:false, d:false, p:false, k:false, b:false };
  private prev: InputSnapshot = { ...this.cur };

  setSnapshot(s: InputSnapshot) { this.prev = this.cur; this.cur = s; }
  update(_t: number) {}
  isLeft()  { return this.cur.l; }
  isRight() { return this.cur.r; }
  isUp()    { return this.cur.u; }
  isDown()  { return this.cur.d; }
  isPunch() { return this.cur.p; }
  isKick()  { return this.cur.k; }
  isBlock() { return this.cur.b; }
  justPressed(action: string): boolean {
    const k = action[0] as keyof InputSnapshot;
    return !!this.cur[k] && !this.prev[k];
  }
}

// Совместимость со старым кодом: для FriendInfo/getMpClient API
export type FriendInfo = PlayerInfo;
